import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography } from "@mui/material"; // Added Typography for cleaner text rendering
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import loaderGif from '../cyverse_waiting.gif';

interface MessageDto {
// 11 |     id: string | number;
  content: string;
  isUser: boolean;
  type?: 'text' | 'loading';
}

interface MessageProps {
  message: MessageDto;
  onNavigate?: (url: string) => void;
}

const CodeBlock = ({ node, className, children, ...props }: any) => {
  const match = /language-(\w+)/.exec(className || '');
  const codeText = String(children).replace(/\n$/, '');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (copied) {
      timer = setTimeout(() => setCopied(false), 2000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [copied]);

  return match ? (
    <div style={{ position: 'relative', textAlign: 'left', width: '100%' }}>
      <SyntaxHighlighter
        style={oneDark}
        language={match[1]}
        PreTag="div"
      >
        {codeText}
      </SyntaxHighlighter>
      <CopyToClipboard text={codeText} onCopy={() => setCopied(true)}>
        <button
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            // ... your button styles
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </CopyToClipboard>
    </div>
  ) : (
    <code className={className} style={{ whiteSpace: 'pre-wrap' }}>
      {children}
    </code>
  );
};

const Message: React.FC<MessageProps> = ({ message, onNavigate }) => {
  const handleLinkClick = (href: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(href);
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const isSystemLoadingMessage = message.type === 'loading' && !message.isUser;

  return (
    <Box
      sx={{
        textAlign: message.isUser ? "right" : "left",
        margin: "8px"
      }}
    >
      <Box
        sx={{
          display: 'inline-block',
          color: message.isUser ? "#ffffff" : "#000000",
          backgroundColor: message.isUser ? "#1186fe" : "#eaeaea",
          padding: "15px",
          borderRadius: "8px",
          maxWidth: "70%",
          wordBreak: "break-word"
        }}
      >
        {isSystemLoadingMessage ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img
              src={loaderGif}
              alt="Loading"
              style={{
                width: '20px',
                height: '20px',
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" component="span" sx={{ fontStyle: 'italic', marginLeft: '8px', lineHeight: 'normal' }}>
              {message.content}
            </Typography>
          </Box>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children }) => (
                <a
                  href={href}
                  onClick={href ? handleLinkClick(href) : undefined}
                  style={{
                    color: message.isUser ? "#ffffff" : "#0066cc",
                    textDecoration: "underline",
                    cursor: "pointer"
                  }}
                >
                  {children}
                </a>
              ),
              code: CodeBlock
            }}
          >
            {message.content}
          </ReactMarkdown>
        )}
      </Box>
    </Box>
  );
};

export default Message;